import React, { useCallback, useEffect, useMemo } from 'react'
import {
  Collapse,
  Popover,
  Slider,
  Form,
  Radio,
  Tooltip,
  Divider,
  Input,
  CollapsePanelProps
} from 'antd'
import {
  CameraOutlined,
  CheckOutlined,
  InfoCircleOutlined
} from '@ant-design/icons'
import classnames from 'classnames'
import { SketchPicker } from 'react-color'
import { subWatermarkText } from '../../../../utils/utils'
import { setEditorWatermark } from '../../../../actions/watermark'
import sensors, { parsePlaceValue } from '../../../../utils/sensors'

import TitleContentItem from './TitleContentItem'
import ImageCrop from '../../../components/ImageCrop'
import { readFileAsDataUrl } from '../../../../utils/imageUtils'
import no_cover_img from '../../../../images/editor/no_cover.jpg'
import lucency_img from '../../../../images/editor/lucency.jpg'
import half_opacity_img from '../../../../images/editor/half_opacity.jpg'
import crown_golden_img from '../../../../images/crown_golden.png'
import colorPlate_img from '../../../../images/watermark/colorPlate.png'

import {
  ADDRESS_STYLES,
  BASE_ID,
  DATE_TIME_STYLES,
  GPS_STYLES,
  ITEM_ID,
  PRESET_COLORS,
  WATERMARK_CONFIG,
  WEATHER_STYLES,
  calculateScale,
  calculateSlider
} from '../../../../constants/watermark'
import { useAppDispatch, useAppSelector } from '../../../../app/hooks'
import { setPaymentVisible } from '../../../../actions/main'

const { Panel } = Collapse
const FormItem = Form.Item
const RadioGroup = Radio.Group

// 条目配置类型定义
interface ItemConfigBase {
  tooltip?: string
  styles?: Array<{ id: number; content: string; example: string }>
  options?: number[]
  cardStyle?: React.CSSProperties
  titleEditable?: boolean
  contentMaxSize?: number
  titleMaxSize?: number
  forbidEmployeeModify?: boolean
  switchDisabled?: boolean
}

// 面板 Props 类型定义
interface PanelPropsBase extends Omit<CollapsePanelProps, 'header'> {
  clickEvent?: (event: string) => void
}

interface LogoPanelProps extends PanelPropsBase {
  logoSourceSrc: string
  onPickImage: (src: string) => void
  onCropComplete: () => void
}

interface ItemPanelProps extends PanelPropsBase {
  item: any
  index: number
}

interface OptionsPanelProps extends ItemPanelProps {
  id: string
  clickEvent: (event: string) => void
}

const useSizeScaleVisible = (baseId, watermark, theme) => {
  //ID20，工程水印，透明度切换到无遮挡模式时，去掉【水印大小】修改功能
  if (baseId === BASE_ID.ID_20 && watermark.theme?.alpha === '0') {
    return false
  }
  return theme?.editable?.includes('sizeScale')
}

// 目前只有ID20可以修改水印大小（含字体大小）
export const ThemePanelContent = (props) => {
  const watermark = useAppSelector((state) => state.editor.watermark)
  const dispatch = useAppDispatch()
  const baseId = watermark.base_id
  const theme = WATERMARK_CONFIG[baseId].theme
  const sizeScaleVisible = useSizeScaleVisible(baseId, watermark, theme)
  const isId20 = baseId === BASE_ID.ID_20

  const onChange = (newWatermark) => dispatch(setEditorWatermark(newWatermark))

  // ID20 水印的主题色选择器 - 预设颜色平铺 + 彩色盘
  const renderId20ColorPicker = () => {
    const currentColor = watermark.theme?.color

    return (
      <div className="color-picker-wrapper">
        <span>主题色：</span>
        <div className="id20-color-picker">
          <div className="preset-colors">
            {PRESET_COLORS.map((color) => (
              <span
                key={color}
                className={`color-circle ${
                  currentColor === color ? 'active' : ''
                }`}
                style={{ backgroundColor: color }}
                onClick={() => {
                  props.clickEvent('chooseColor')
                  onChange({
                    ...watermark,
                    theme: { ...watermark.theme, color }
                  })
                }}
              >
                {currentColor === color && <CheckOutlined />}
              </span>
            ))}
            <Popover
              classNames={{ root: 'sketch-picker-container' }}
              trigger="click"
              content={
                <SketchPicker
                  presetColors={PRESET_COLORS}
                  disableAlpha
                  width={230}
                  color={currentColor}
                  onChangeComplete={(color) => {
                    props.clickEvent('chooseColor')
                    onChange({
                      ...watermark,
                      theme: { ...watermark.theme, color: color.hex }
                    })
                  }}
                />
              }
              placement="rightBottom"
            >
              <span
                className="more-colors"
                onClick={() => props.clickEvent('themeColor')}
              >
                <img src={colorPlate_img} alt="更多颜色" />
              </span>
            </Popover>
          </div>
        </div>
      </div>
    )
  }

  // 其他水印的主题色选择器 - 保持原有逻辑
  const renderNormalColorPicker = () => {
    return (
      <div className="color-picker-wrapper">
        <span>主题色：</span>
        <Popover
          classNames={{ root: 'sketch-picker-container' }}
          trigger="click"
          content={
            <SketchPicker
              presetColors={PRESET_COLORS}
              disableAlpha
              width={230}
              color={watermark.theme?.color}
              onChangeComplete={(color) =>
                onChange({
                  ...watermark,
                  theme: { ...watermark.theme, color: color.hex }
                })
              }
            />
          }
          placement="rightBottom"
        >
          <span
            className="color-button-wrapper"
            onClick={() => props.clickEvent('themeColor')}
          >
            <div className="color-button">
              <div
                style={{
                  backgroundColor: watermark.theme?.color
                }}
              ></div>
            </div>
          </span>
        </Popover>
      </div>
    )
  }

  return (
    <div className="form-item-theme-container">
      <div>
        {theme?.editable.includes('color') &&
          (isId20 ? renderId20ColorPicker() : renderNormalColorPicker())}
        {theme?.editable.includes('textColor') && (
          <div className="color-picker-wrapper" style={{ marginLeft: '56px' }}>
            <span>字体色：</span>
            <Popover
              classNames={{ root: 'sketch-picker-container' }}
              trigger="click"
              content={
                <SketchPicker
                  presetColors={PRESET_COLORS}
                  disableAlpha
                  width={230}
                  color={watermark.theme?.textColor}
                  onChangeComplete={(color) => {
                    onChange({
                      ...watermark,
                      theme: { ...watermark.theme, textColor: color.hex }
                    })
                  }}
                />
              }
              placement="rightBottom"
            >
              <span
                className="color-button-wrapper"
                onClick={() => props.clickEvent('textColor')}
              >
                <div className="color-button">
                  <div
                    style={{
                      backgroundColor: watermark.theme?.textColor
                    }}
                  ></div>
                </div>
              </span>
            </Popover>
          </div>
        )}
      </div>
      {sizeScaleVisible && (
        <div className="slider-controller-wrapper">
          <span style={{ width: '70px', textAlign: 'right' }}>水印大小：</span>
          <Slider
            value={calculateSlider(Number(watermark.theme?.sizeScale), baseId)}
            style={{ width: '250px' }}
            tooltip={{ open: false }}
            min={0}
            max={2}
            step={0.01}
            marks={{ 0: '小', 1: '标准', 2: '大' }}
            onChange={(value: number) => {
              const { fontScale, sizeScale } = calculateScale(value, baseId)
              onChange({
                ...watermark,
                theme: {
                  ...watermark.theme,
                  sizeScale: String(Math.round(sizeScale * 100) / 100),
                  fontScale: String(fontScale)
                }
              })
            }}
          />
        </div>
      )}
      {theme?.editable.includes('alpha') && (
        <div className="opacity-controller-wrapper">
          <span>透明度：</span>
          <div className="opacity-controller-items">
            <div className="item">
              <img src={half_opacity_img} alt="" />
              <Radio
                checked={watermark.theme?.alpha === '0.5'}
                onClick={() => {
                  onChange({
                    ...watermark,
                    theme: { ...watermark.theme, alpha: '0.5' }
                  })
                  props.clickEvent('semiTranslucent')
                }}
              >
                半透明
              </Radio>
            </div>
            <div className="item">
              <img src={lucency_img} alt="" />
              <Radio
                checked={watermark.theme?.alpha === '0.1'}
                onClick={() => {
                  onChange({
                    ...watermark,
                    theme: { ...watermark.theme, alpha: '0.1' }
                  })
                  props.clickEvent('fullyTransparent')
                }}
              >
                全透明
              </Radio>
            </div>
            <div className="item">
              <img src={no_cover_img} alt="" />
              <Radio
                checked={watermark.theme?.alpha === '0'}
                onClick={() => {
                  onChange({
                    ...watermark,
                    theme: { ...watermark.theme, alpha: '0' }
                  })
                  props.clickEvent('shelterless')
                }}
              >
                无遮挡
              </Radio>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export const LogoPanel = (props: LogoPanelProps) => {
  const { logoSourceSrc, onPickImage, onCropComplete, ...panelProps } = props
  const watermark = useAppSelector((state) => state.editor.watermark)
  const dispatch = useAppDispatch()
  const logo = watermark.logo?.url
  const baseId = watermark.base_id
  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      readFileAsDataUrl(e.target.files[0]).then((dataUrl) => {
        onPickImage(dataUrl as string)
        const inputEl = document.getElementById(
          'editor-image-input'
        ) as HTMLInputElement | null
        if (inputEl) inputEl.value = ''
      })
    }
  }
  const onChange = (newWatermark: any) =>
    dispatch(setEditorWatermark(newWatermark))
  return (
    <Panel
      {...panelProps}
      header={(WATERMARK_CONFIG[baseId]?.logo as any)?.displayName}
    >
      <div className="form-item-logo-container">
        <input
          id="editor-image-input"
          style={{ display: 'none' }}
          type="file"
          accept="image/*"
          onChange={onSelectFile}
        />
        <div
          className="upload-logo"
          style={logo ? { backgroundImage: `url(${logo})` } : {}}
          onClick={() => document.getElementById('editor-image-input')?.click()}
        >
          {!logo && (
            <>
              <CameraOutlined style={{ fontSize: '30px' }} />
              <span>点击上传</span>
            </>
          )}
        </div>
        <ImageCrop
          src={logoSourceSrc}
          onCancel={() => onPickImage('')}
          onOk={(image) => {
            onChange({
              ...watermark,
              logo: { ...watermark.logo, url: image }
            })
            onCropComplete()
          }}
        />
        {(WATERMARK_CONFIG[baseId]?.logo as any)?.editable?.includes(
          'scale'
        ) && (
          <div className="slider-controller-wrapper">
            <span style={{ width: '70px', textAlign: 'right' }}>
              图片大小：
            </span>
            <Slider
              value={parseInt(String(watermark.logo?.scale * 100))}
              style={{ width: '250px' }}
              tooltip={{ open: false }}
              min={10}
              max={100}
              onChange={(value: number) =>
                onChange({
                  ...watermark,
                  logo: { ...watermark.logo, scale: String(value / 100) }
                })
              }
            />
            <span style={{ width: '36px', marginLeft: '5px' }}>
              {parseInt(String(watermark.logo?.scale * 100))}%
            </span>
          </div>
        )}
        {(WATERMARK_CONFIG[baseId]?.logo as any)?.editable?.includes(
          'alpha'
        ) && (
          <div className="slider-controller-wrapper">
            <span style={{ width: '70px', textAlign: 'right' }}>透明度：</span>
            <Slider
              value={100 - parseInt(String(watermark.logo?.alpha * 100))}
              style={{ width: '250px' }}
              tooltip={{ open: false }}
              min={0}
              max={100}
              onChange={(value: number) =>
                onChange({
                  ...watermark,
                  logo: {
                    ...watermark.logo,
                    alpha: String((100 - value) / 100)
                  }
                })
              }
            />
            <span style={{ width: '36px', marginLeft: '5px' }}>
              {100 - parseInt(String(watermark.logo?.alpha * 100))}%
            </span>
          </div>
        )}
      </div>
    </Panel>
  )
}

export const CustomItemPanel = (props: ItemPanelProps) => {
  const { item, ...panelProps } = props
  return (
    <Panel {...panelProps} header={item.title}>
      <TitleContentItem {...props} />
    </Panel>
  )
}

export const SNPanel = (props: ItemPanelProps) => {
  const { item, index, ...panelProps } = props
  const watermark = useAppSelector((state) => state.editor.watermark)
  const dispatch = useAppDispatch()
  const onChange = (newWatermark: any) =>
    dispatch(setEditorWatermark(newWatermark))
  const itemConfig = WATERMARK_CONFIG[watermark.base_id]?.[item.id] as
    | ItemConfigBase
    | undefined
  const titleMaxLength = itemConfig?.titleMaxSize ?? 20
  return (
    <Panel {...panelProps} header={item.title}>
      <div className="form-custom-item-container">
        <Form>
          <FormItem label="标题">
            <Input
              value={item.title}
              onChange={(e) => {
                const newItems = watermark.items.map((it: any, idx: number) =>
                  idx === index
                    ? {
                        ...it,
                        title: subWatermarkText(e.target.value, titleMaxLength)
                      }
                    : it
                )
                onChange({
                  ...watermark,
                  items: newItems
                })
              }}
            />
          </FormItem>
          <FormItem label="编号示例">
            <Input value="114-009-1354" disabled />
            <p
              style={{
                lineHeight: 2,
                marginTop: 10,
                color: 'rgba(0, 0, 0, 0.5)'
              }}
            >
              编号自动生成，不可修改。
              <br />
              每个编号唯一，在团队中可通过编号快速搜索照片。
            </p>
          </FormItem>
        </Form>
      </div>
    </Panel>
  )
}

export const OptionsPanel = (props: OptionsPanelProps) => {
  const { item, index, id, clickEvent, ...panelProps } = props
  const watermark = useAppSelector((state) => state.editor.watermark)
  const isVip = useAppSelector((state) => state.group.isVip)
  const groupID = useAppSelector((state) => state.group.groupId)
  const dispatch = useAppDispatch()
  const onChange = (newWatermark: any) =>
    dispatch(setEditorWatermark(newWatermark))
  const itemConfig = WATERMARK_CONFIG[watermark.base_id]?.[item.id] as
    | ItemConfigBase
    | undefined
  const expandedPanels = useAppSelector((state) => state.editor.expandedPanels)

  const onForbidEmployeeModifyChange = useCallback(
    (e: any) => {
      if (!isVip && e.target.value === 0) {
        dispatch(setPaymentVisible(true, 'WEBWatermarkMemberTuneLocation'))
        sensors.track('web_click_button_workgroup_go_vip_trigger', {
          place: parsePlaceValue(),
          functionType: 'memberTuneLocation',
          groupID
        })
      } else {
        const newItems = watermark.items.map((it: any, idx: number) =>
          idx === index ? { ...it, editType: e.target.value } : it
        )
        onChange({
          ...watermark,
          items: newItems
        })
        if (e.target.value === 4) {
          clickEvent('openMemberTuneLocation')
        } else if (e.target.value === 0) {
          clickEvent('closeMemberTuneLocation')
        }
      }
    },
    [isVip, watermark, index, groupID, clickEvent, dispatch, onChange]
  )

  const curPanelExpanded = useMemo(
    () => expandedPanels.includes(id),
    [expandedPanels, id]
  )

  const onReport = () => {
    if (itemConfig?.styles === DATE_TIME_STYLES) {
      clickEvent('chooseTimeStyle')
    } else if (itemConfig?.styles === WEATHER_STYLES) {
      clickEvent('chooseWeatherStyle')
    } else if (itemConfig?.styles === ADDRESS_STYLES) {
      clickEvent('chooseLoactionStyle')
    } else if (itemConfig?.styles === GPS_STYLES) {
      clickEvent('chooseLatStyle')
    }
  }

  useEffect(() => {
    if (
      curPanelExpanded &&
      item.id === ITEM_ID.ID_2 &&
      !itemConfig?.forbidEmployeeModify &&
      !isVip
    ) {
      sensors.track('web_show_page_openapp_workgroupvip', {
        groupID: groupID,
        place: parsePlaceValue(),
        functionType: 'memberTuneLocation'
      })
    }
  }, [
    curPanelExpanded,
    item.id,
    itemConfig?.forbidEmployeeModify,
    isVip,
    groupID
  ])

  return (
    <Panel
      {...panelProps}
      header={
        <span>
          {item.title}&nbsp;&nbsp;
          <Tooltip title={itemConfig?.tooltip}>
            <span>
              <InfoCircleOutlined style={{ color: '#818592' }} />
            </span>
          </Tooltip>
        </span>
      }
    >
      <div className="form-options-item-container">
        {(itemConfig?.styles || [])
          .filter((style) =>
            itemConfig?.options?.length
              ? itemConfig.options.includes(style.id)
              : true
          )
          .map((style) => (
            <div
              key={style.id}
              className={classnames('options-style-item', {
                active: item.style === style.id
              })}
              style={itemConfig?.cardStyle}
              onClick={() => {
                const newItems = watermark.items.map((it: any, idx: number) =>
                  idx === index
                    ? { ...it, style: style.id, content: style.content }
                    : it
                )
                onChange({
                  ...watermark,
                  items: newItems
                })
                onReport()
              }}
            >
              <span className="example">{style.example}</span>
              <span className="desc">{style.content}</span>
            </div>
          ))}
        {item.id === ITEM_ID.ID_2 && !itemConfig?.forbidEmployeeModify && (
          <>
            <Divider />
            <Form layout="inline">
              <FormItem label="员工是否可对实际地点进行微调">
                <RadioGroup
                  value={item.editType}
                  onChange={onForbidEmployeeModifyChange}
                >
                  <Radio value={4}>是</Radio>
                  <Radio value={0}>否</Radio>
                </RadioGroup>
                {isVip ? (
                  <img style={{ width: '18px' }} src={crown_golden_img} />
                ) : (
                  <span
                    style={{
                      borderRadius: '2px',
                      border: '0.5px solid #E49B2D',
                      background: '#FFF8EE',
                      padding: '2px 8px',
                      color: '#A36B48',
                      fontSize: '14px',
                      lineHeight: '22px'
                    }}
                  >
                    开通<strong>团队会员</strong>即可解锁
                  </span>
                )}
              </FormItem>
            </Form>
          </>
        )}
      </div>
    </Panel>
  )
}
